/**
 * Who may sit down, who already has, and what is left to do — kept out of the
 * components so the rules can be tested without a render harness.
 *
 * The rules that are not obvious from the screen:
 *
 *  1. A guest who is already in a chair is NEVER offered a second one. The
 *     database enforces one seat per guest per chart (UNIQUE(chart_id,
 *     invitee_id)), so a picker that offers a seated name is offering a button
 *     that answers 409.
 *  2. "Seated" is read from the chairs, not only from the candidate's own
 *     `seated` flag. Both come from the same bulk read, but the chairs are what
 *     the unique index is actually built on.
 *  3. A dinner claim carries a party size, so a claim of three needs three
 *     chairs: one for the guest and two plus-one chairs pointing back at them.
 *     Those are offered as their own picker rows, because a host seating a
 *     table thinks in chairs.
 *  4. Nobody is DROPPED for not having answered, but the people who said yes
 *     are counted and listed apart from the people who did not. Ranking alone
 *     was the original answer here and it was not enough: on the Chapter 40
 *     trip, 137 invitees with ~40 yeses, it put the right names on top and left
 *     97 underneath with nothing on screen marking where one group ended, so
 *     "who am I still missing" could not be read off the panel at all. Progress
 *     counts the attending; the rest are kept in `unseatedOther`, shown behind
 *     a count, and remain one tap from a chair — a guest who never replied and
 *     turns up is a real problem and the host decides, not this module.
 *  5. A claim the host typed in has no invitee row, so there is no id on either
 *     side to key rule 1 on: the chair it writes is a named guest chair, and the
 *     server's `seated` flag stays false for it forever. Those are matched back
 *     by folded name, or such a guest is offered again after every refetch and
 *     the host seats them at a second table believing the first tap was lost.
 */
import { normalizeName } from './invitee-match';
import type {
  AssignSeatRequest,
  SeatAssignment,
  SeatingCandidate,
  SeatingTable,
} from '../types/seating';

/** Chairs of one table, by ordinal. Absent ordinals are empty chairs. */
export function seatMapFor(table: SeatingTable): Map<number, SeatAssignment> {
  const map = new Map<number, SeatAssignment>();
  for (const seat of table.seats ?? []) {
    if (seat.assignment) map.set(seat.seatIndex, seat.assignment);
  }
  return map;
}

/** Every invitee sitting somewhere in this chart. */
export function seatedInviteeIds(tables: SeatingTable[]): Set<string> {
  const seated = new Set<string>();
  for (const table of tables) {
    for (const assignment of seatMapFor(table).values()) {
      if (assignment.inviteeId) seated.add(assignment.inviteeId);
    }
  }
  return seated;
}

/** Plus-one chairs already placed, per claimer. */
export function companionCountsByInvitee(tables: SeatingTable[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const table of tables) {
    for (const assignment of seatMapFor(table).values()) {
      const claimer = assignment.accompaniesInviteeId;
      if (assignment.seatKind === 'guest' && claimer) {
        counts.set(claimer, (counts.get(claimer) ?? 0) + 1);
      }
    }
  }
  return counts;
}

/**
 * The names on chairs that stand for a person in their own right — a named guest
 * chair with no claimer behind it, which is exactly what seatRequestForCandidate
 * writes for an invitee-less claim. Plus-one chairs are excluded: "Jess's +1"
 * belongs to Jess, not to a candidate of that name.
 */
export function seatedGuestNames(tables: SeatingTable[]): Set<string> {
  const seated = new Set<string>();
  for (const table of tables) {
    for (const assignment of seatMapFor(table).values()) {
      if (assignment.seatKind !== 'guest' || assignment.accompaniesInviteeId) continue;
      const name = normalizeName(assignment.guestLabel || assignment.displayName);
      if (name) seated.add(name);
    }
  }
  return seated;
}

export function isSeated(
  candidate: SeatingCandidate,
  seated: Set<string>,
  guestNames: Set<string> = new Set(),
): boolean {
  if (candidate.seated) return true;
  if (candidate.inviteeId) return seated.has(candidate.inviteeId);
  // No invitee id on either side, and the server's `seated` flag is only ever set
  // for rows that have one — so their chair is the only record that they sat
  // down. Folded with the importer's normalizeName because the label on the chair
  // is the display name round-tripped through the API.
  return guestNames.has(normalizeName(candidate.displayName));
}

const RSVP_RANK: Record<string, number> = { going: 0, maybe: 1, unknown: 2 };

/** going, then maybe, then everyone who never answered; alphabetical inside each. */
export function rankCandidates(candidates: SeatingCandidate[]): SeatingCandidate[] {
  return [...candidates].sort((a, b) => {
    // A guest part-way out of the dinner sinks below everyone still in it: their
    // chair is the one the host is most likely to want back.
    const releasing = Number(a.claimStatus === 'release_pending') - Number(b.claimStatus === 'release_pending');
    if (releasing !== 0) return releasing;
    const rank = (RSVP_RANK[a.rsvpStatus ?? ''] ?? 3) - (RSVP_RANK[b.rsvpStatus ?? ''] ?? 3);
    if (rank !== 0) return rank;
    return a.displayName.localeCompare(b.displayName);
  });
}

function matchesQuery(candidate: SeatingCandidate, needle: string): boolean {
  if (!needle) return true;
  const haystack = normalizeName(
    [candidate.displayName, candidate.email, candidate.phone].filter(Boolean).join(' '),
  );
  return haystack.includes(needle);
}

/**
 * The attending guests still without a chair, narrowed by what the host typed.
 * Folds with the importer's `normalizeName` so a roster pasted out of Partiful —
 * non-breaking spaces, curly apostrophes — is findable by typing it plainly.
 */
export function pickerCandidates(
  candidates: SeatingCandidate[],
  tables: SeatingTable[],
  query = '',
  /**
   * Show only the guests who said yes.
   *
   * Ranking was supposed to be enough — going first, then maybe, then no reply,
   * never shortening the list. On a roster of 137 with 40 yeses it is not:
   * ranking puts the right names on top and leaves 97 underneath them, and the
   * host seating a table cannot tell where the people who are actually coming
   * stop. A filter answers that; a sort never could.
   *
   * Off, it is the old behaviour exactly — which is still the right one for a
   * trip where nobody replies and the host is seating from memory.
   */
  goingOnly = false,
): SeatingCandidate[] {
  const seated = seatedInviteeIds(tables);
  const guestNames = seatedGuestNames(tables);
  const needle = normalizeName(query);
  return rankCandidates(
    candidates.filter((candidate) => !isSeated(candidate, seated, guestNames)),
  )
    .filter((candidate) => !goingOnly || isAttending(candidate))
    .filter((candidate) => matchesQuery(candidate, needle));
}

/**
 * Counted as coming.
 *
 * A guest holding a dinner claim on this table is attending whatever the roster
 * says — they picked a seat. That is a stronger signal than an RSVP field, and
 * without it the filter would hide somebody who has literally claimed a chair.
 */
export function isAttending(candidate: SeatingCandidate): boolean {
  if (candidate.claimStatus) return true;
  return String(candidate.rsvpStatus ?? '').trim().toLowerCase() === 'going';
}

export interface CompanionSlot {
  /** The claimer this chair belongs to; never null — a plus-one needs someone to be plus one of. */
  inviteeId: string;
  claimerName: string;
  /** "Jess's +1", which is what goes on the chair. */
  guestLabel: string;
  /** How many of this claimer's companion chairs are still unplaced. */
  remaining: number;
}

/**
 * The plus-one chairs a claimer is still owed.
 *
 * Capped at party_size - 1 because that is what the write transaction enforces;
 * offering a chair the API answers 409 party_size_exceeded to is worse than not
 * offering it. Only offered once the claimer is seated: a lone "+1" chair at a
 * table whose guest is not there yet is how a host loses track of a party.
 */
export function companionSlots(
  candidates: SeatingCandidate[],
  tables: SeatingTable[],
  query = '',
): CompanionSlot[] {
  const seated = seatedInviteeIds(tables);
  const placed = companionCountsByInvitee(tables);
  const needle = normalizeName(query);

  return rankCandidates(candidates)
    .filter((candidate) => matchesQuery(candidate, needle))
    .flatMap((candidate) => {
      const inviteeId = candidate.inviteeId;
      if (!inviteeId || !seated.has(inviteeId)) return [];
      const remaining = Math.max(candidate.seatsNeeded - 1 - (placed.get(inviteeId) ?? 0), 0);
      if (remaining <= 0) return [];
      return [
        {
          inviteeId,
          claimerName: candidate.displayName,
          guestLabel: `${candidate.displayName}'s +1`,
          remaining,
        },
      ];
    });
}

export interface SeatingProgress {
  /** Attending guests with a chair, out of everyone who could have one. */
  seatedGuests: number;
  totalGuests: number;
  /** Chairs filled out of chairs that exist — including plus-ones and blocked chairs. */
  chairsUsed: number;
  chairsTotal: number;
  /** Chairs the attending list still needs, plus-ones included. */
  seatsNeeded: number;
  /**
   * Who is coming and has no chair — the answer to "who am I still missing".
   *
   * ONLY the attending. This used to be every candidate without a seat, which
   * on a roster of 137 with 40 yeses meant 99 names, of whom 97 were never
   * coming. The two people the host was actually looking for were somewhere in
   * that list, below a fold set at twelve.
   */
  unseated: SeatingCandidate[];
  /**
   * The rest of the roster, still without a chair. Kept and counted rather than
   * dropped: a guest who never replied and turns up is a real problem, and the
   * host is the one who decides whether to seat them.
   */
  unseatedOther: SeatingCandidate[];
}

/**
 * The two numbers a host actually asks for: how many of my people are seated,
 * and have I put out enough chairs. Deliberately separate — a chart with every
 * guest seated can still be short of chairs for their plus-ones.
 */
export function seatingProgress(
  candidates: SeatingCandidate[],
  tables: SeatingTable[],
): SeatingProgress {
  const seated = seatedInviteeIds(tables);
  const guestNames = seatedGuestNames(tables);
  const allUnseated = rankCandidates(
    candidates.filter((candidate) => !isSeated(candidate, seated, guestNames)),
  );
  // Every number here is about the people who are coming. Counting the whole
  // roster made "seated" read 38 of 137 on a party of 40 — a progress bar that
  // can never fill and a shortfall that is not real.
  const attending = candidates.filter(isAttending);
  const unseated = allUnseated.filter(isAttending);
  const unseatedOther = allUnseated.filter((candidate) => !isAttending(candidate));

  let chairsUsed = 0;
  let chairsTotal = 0;
  for (const table of tables) {
    chairsTotal += table.capacity;
    chairsUsed += seatMapFor(table).size;
  }

  return {
    seatedGuests: attending.length - unseated.length,
    totalGuests: attending.length,
    chairsUsed,
    chairsTotal,
    seatsNeeded: attending.reduce((sum, candidate) => sum + Math.max(candidate.seatsNeeded, 1), 0),
    unseated,
    unseatedOther,
  };
}

/**
 * What to PUT for this candidate.
 *
 * A claim entered by the host carries no invitee id (the schema allows it), and
 * such a person can only ever be a named guest chair: UNIQUE(chart_id,
 * invitee_id) has nothing to key on for them.
 */
export function seatRequestForCandidate(candidate: SeatingCandidate): AssignSeatRequest {
  if (candidate.inviteeId) {
    return { seatKind: 'invitee', inviteeId: candidate.inviteeId };
  }
  return { seatKind: 'guest', guestLabel: candidate.displayName };
}

/**
 * What to PUT to put this exact occupant somewhere else.
 *
 * A move is an assign, not an unassign-then-assign: the server vacates the old
 * chair in the same transaction, so there is no moment where the guest is
 * nowhere and no half-finished move if the second call never happens.
 *
 * Every kind names the chair it is leaving. UNIQUE(chart_id, invitee_id) is
 * enough for the server to find an invitee's old chair on its own, but a
 * plus-one has no invitee_id and no chart-unique label — a claim of four
 * legitimately holds three chairs reading "Jess's +1" — so a move that does not
 * say where it came from is indistinguishable from adding another guest, and
 * lands as a second chair.
 */
export function seatRequestForAssignment(assignment: SeatAssignment): AssignSeatRequest {
  if (assignment.seatKind === 'guest') {
    return {
      seatKind: 'guest',
      accompaniesInviteeId: assignment.accompaniesInviteeId ?? null,
      guestLabel: assignment.guestLabel || assignment.displayName || 'Guest',
      fromAssignmentId: assignment.id,
    };
  }
  if (assignment.seatKind === 'blocked') {
    return { seatKind: 'blocked', fromAssignmentId: assignment.id };
  }
  return {
    seatKind: 'invitee',
    inviteeId: assignment.inviteeId ?? null,
    fromAssignmentId: assignment.id,
  };
}

/** The name on the chair, whichever kind of chair it is. */
export function occupantName(assignment: SeatAssignment): string {
  if (assignment.seatKind === 'blocked') return 'Kept empty';
  return assignment.displayName || assignment.guestLabel || 'Guest';
}

/** Why a chair is flagged, in words a host can act on. '' when it is fine. */
export function attendanceNote(status?: string | null): string {
  switch (status) {
    case 'released':
      return 'gave their dinner spot back — still seated here';
    case 'cancelled':
      return 'this dinner was cancelled — still seated here';
    case 'not_claimed':
      return 'has not claimed a spot at this dinner';
    default:
      return '';
  }
}
